import { Form } from '../components/ui/validated-form';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { authAPI, getUserErrorMessage } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const SESSION_RETRY_LIMIT = 8;

export default function SetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [resolving, setResolving] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [resolvedEmail, setResolvedEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const resetToken = (searchParams.get('token') || '').trim();

  const flow = useMemo(
    () => (searchParams.get('flow') === 'recovery' ? 'recovery' : 'invite'),
    [searchParams]
  );

  useEffect(() => {
    let mounted = true;
    let attempts = 0;
    let timer = null;

    if (resetToken) {
      setSessionReady(true);
      setResolving(false);
      setErrorMessage('');
      return () => {
        mounted = false;
      };
    }

    const cleanupAuthParamsFromUrl = () => {
      const nextParams = new URLSearchParams();
      if (flow) nextParams.set('flow', flow);
      const nextSearch = nextParams.toString();
      const cleanUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
      window.history.replaceState({}, document.title, cleanUrl);
    };

    const resolveSessionFromUrl = async () => {
      const hash = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
      const hashParams = new URLSearchParams(hash);
      const code = searchParams.get('code') || hashParams.get('code');
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const tokenHash = searchParams.get('token_hash') || hashParams.get('token_hash');
      const recoveryType = searchParams.get('type') || hashParams.get('type');

      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) throw error;
        cleanupAuthParamsFromUrl();
        return true;
      }

      if (accessToken && refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (error) throw error;
        cleanupAuthParamsFromUrl();
        return true;
      }

      if (tokenHash && (recoveryType === 'recovery' || flow === 'recovery')) {
        const { error } = await supabase.auth.verifyOtp({
          type: 'recovery',
          token_hash: tokenHash,
        });
        if (error) throw error;
        cleanupAuthParamsFromUrl();
        return true;
      }

      return false;
    };

    const resolveSession = async () => {
      if (!supabase) {
        if (mounted) {
          setResolving(false);
          setErrorMessage('Supabase ist nicht konfiguriert.');
        }
        return true;
      }

      try {
        await resolveSessionFromUrl();
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        const session = data?.session || null;
        if (session?.user) {
          if (!mounted) return true;
          setResolvedEmail(session.user.email || '');
          setSessionReady(true);
          setResolving(false);
          setErrorMessage('');
          return true;
        }
      } catch (err) {
        if (!mounted) return true;
        setResolving(false);
        setErrorMessage(err?.message || 'Link konnte nicht verarbeitet werden.');
        return true;
      }

      attempts += 1;
      if (attempts >= SESSION_RETRY_LIMIT) {
        if (mounted) {
          setResolving(false);
          setErrorMessage(
            flow === 'recovery'
              ? 'Der Link ist ungültig oder abgelaufen. Fordern du bitte einen neuen Passwort-Reset an.'
              : 'Die Einladung konnte nicht geladen werden. Bitte den Owner, die Einladung erneut zu senden.'
          );
        }
        return true;
      }
      return false;
    };

    resolveSession().then((done) => {
      if (done) return;
      timer = setInterval(async () => {
        const finished = await resolveSession();
        if (finished) clearInterval(timer);
      }, 500);
    });

    if (!supabase) {
      return () => {
        mounted = false;
        if (timer) clearInterval(timer);
      };
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted || !session?.user) return;
      setResolvedEmail(session.user.email || '');
      setSessionReady(true);
      setResolving(false);
      setErrorMessage('');
    });

    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
      subscription?.unsubscribe();
    };
  }, [flow, location.pathname, resetToken, searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen haben');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);
    try {
      if (resetToken) {
        await authAPI.resetPasswordConfirm({ token: resetToken, password });
        toast.success('Passwort wurde gesetzt. Bitte melde dich an.');
        navigate('/login', { replace: true });
        return;
      }

      if (!supabase) throw new Error('Supabase ist nicht konfiguriert.');
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      try {
        await authAPI.completeOnboarding({ kind: 'password_setup' });
      } catch (err) {
        console.warn('completeOnboarding failed:', err);
      }

      await supabase.auth.signOut();
      toast.success('Passwort wurde gesetzt. Bitte melde dich an.');
      const next = resolvedEmail
        ? `/login?invite=accepted&email=${encodeURIComponent(resolvedEmail)}`
        : '/login?invite=accepted';
      navigate(next, { replace: true });
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      let displayMsg = 'Fehler beim Setzen des Passworts';
      if (msg.includes('new password should be different from the old password')) {
        displayMsg = 'Das neue Passwort muss sich vom bisherigen unterscheiden.';
      } else if (msg.includes('password should be at least 6 characters')) {
        displayMsg = 'Passwort muss mindestens 6 Zeichen haben.';
      } else if (msg.includes('invalid') || msg.includes('expired')) {
        displayMsg = 'Link ist ungültig oder abgelaufen. Bitte fordere einen neuen Link an.';
      } else if (err?.message) {
        // Supabase-Fehler kommen hier direkt (ohne Interceptor) und waeren sonst englisch.
        displayMsg = getUserErrorMessage(err, displayMsg);
      }
      toast.error(displayMsg);
    } finally {
      setLoading(false);
    }
  };

  if (resolving) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#0071E3] border-t-transparent mx-auto mb-6" />
          <p className="text-[#86868B]">Link wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!sessionReady) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm text-center">
          <h1 className="text-2xl font-bold text-[#1D1D1F] mb-2">
            {flow === 'recovery' ? 'Passwort-Link ungültig' : 'Einladung nicht verfügbar'}
          </h1>
          <p className="text-[#86868B] mb-6">
            {errorMessage || 'Der Link konnte nicht verarbeitet werden.'}
          </p>
          <div className="space-y-3">
            <Link to="/login" className="block text-[#0071E3] hover:underline">
              Zur Anmeldung
            </Link>
            {flow === 'recovery' && (
              <p className="text-sm text-[#86868B]">
                Fordern du auf der Login-Seite einen neuen Passwort-Reset an.
              </p>
            )}
            {flow !== 'recovery' && (
              <p className="text-sm text-[#86868B]">
                Bitte den Owner, die Einladung erneut zu senden.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-[#1D1D1F] mb-2">
          {flow === 'recovery' ? 'Neues Passwort vergeben' : 'Passwort setzen'}
        </h1>
        <p className="text-[#86868B] mb-6">
          {flow === 'recovery'
            ? 'Bitte vergib ein neues Passwort für dein Konto.'
            : 'Bitte vergib ein Passwort für dein eingeladenes Konto.'}
        </p>
        <Form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">Passwort</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mindestens 6 Zeichen"
              className="input-base"
              required
              minLength={6}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Passwort bestaetigen</Label>
            <Input
              id="confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Passwort wiederholen"
              className="input-base"
              required
              minLength={6}
            />
          </div>
          <Button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? 'Wird gespeichert...' : 'Passwort setzen'}
          </Button>
        </Form>
        <p className="mt-6 text-center text-sm text-[#86868B]">
          <Link to="/login" className="text-[#0071E3] hover:underline">
            Zur Anmeldung
          </Link>
        </p>
      </div>
    </div>
  );
}
